// src/features/admin/pages/AdminProductForm.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAdminProducts } from '../hooks/useAdminProducts';
import { AdminProductService } from '../services/adminProductService';
import { CategoryService } from '../services/adminCategoryService';
import { 
  GENDER_FILTERS, 
  INTENSITY_FILTERS, 
  DEFAULT_PRODUCT 
} from '../../october/constants/productConstants';

const AdminProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const { addProduct, updateProduct } = useAdminProducts();

  // Form state
  const [formData, setFormData] = useState({
    ...DEFAULT_PRODUCT,
    category: '' // Single category selection
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Notes state (for categories with showScentNotes enabled)
  const [topNotes, setTopNotes] = useState(['']);
  const [heartNotes, setHeartNotes] = useState(['']);
  const [baseNotes, setBaseNotes] = useState(['']);

  // Check if selected category has showScentNotes enabled
  const shouldShowScentNotes = () => {
    if (!formData.category) return false;
    const category = categories.find(c => c.id === formData.category);
    return category?.showScentNotes === true || formData.category === 'perfumes';
  };

  // Removed type field - no longer needed

  const inputClass = 'w-full px-4 py-3 bg-brand-black border border-brand-red/30 rounded-xl text-brand-light placeholder:text-brand-light/50 focus:outline-none focus:ring-2 focus:ring-brand-gold/60 transition-all font-body text-base';
  const textareaClass = `${inputClass} resize-none`;
  const selectClass = inputClass;
  const labelClass = 'block text-sm font-body font-medium text-brand-light mb-2';
  const cardClass = 'bg-brand-black/80 rounded-2xl border border-brand-red/25 p-6 lg:p-8 shadow-[0_25px_60px_-35px_rgba(230,0,0,0.35)]';

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoadingCategories(true);
        const cats = await CategoryService.getCategories();
        setCategories(cats.filter(c => c.isActive !== false));
      } catch (err) {
        console.error('Error loading categories:', err);
      } finally {
        setLoadingCategories(false);
      }
    };
    loadCategories();
  }, []);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load existing product for editing
  useEffect(() => {
    if (isEditing && id) {
      loadProduct(id);
    }
  }, [id, isEditing]);

  const loadProduct = async (productId) => {
    try {
      setLoading(true);
      const products = await AdminProductService.getAllProductsAdmin();
      const product = products.find(p => p.id === productId);
      
      if (product) {
        // Use single category (support old categories array for backward compatibility)
        const productCategory = product.category || (product.categories && product.categories[0]) || '';
        setFormData({
          ...product,
          category: productCategory
        });
        setImagePreview(product.image || '');
        
        // Set notes arrays if product has notes
        if (product.notes) {
          setTopNotes(product.notes.top || ['']);
          setHeartNotes(product.notes.heart || ['']);
          setBaseNotes(product.notes.base || ['']);
        } else {
          setTopNotes(['']);
          setHeartNotes(['']);
          setBaseNotes(['']);
        }
      } else {
        setError('Product not found');
      }
    } catch (err) {
      setError('Failed to load product');
      console.error('Load product error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'priceValue' && { price: `R${value}` })
    };

    // Type field removed

    setFormData(newFormData);
  };

  // Handle notes changes (for perfumes)
  const handleNotesChange = (noteType, index, value) => {
    const setters = {
      top: setTopNotes,
      heart: setHeartNotes,
      base: setBaseNotes
    };
    
    const setter = setters[noteType];
    setter(prev => {
      const newNotes = [...prev];
      newNotes[index] = value;
      return newNotes;
    });
  };

  const addNoteField = (noteType) => {
    const setters = {
      top: setTopNotes,
      heart: setHeartNotes,
      base: setBaseNotes
    };
    
    const setter = setters[noteType];
    setter(prev => [...prev, '']);
  };

  const removeNoteField = (noteType, index) => {
    const setters = {
      top: setTopNotes,
      heart: setHeartNotes,
      base: setBaseNotes
    };
    
    const setter = setters[noteType];
    setter(prev => prev.filter((_, i) => i !== index));
  };

  // Handle image upload
  const handleImageChange = (file) => {
    if (file && file.type.startsWith('image/')) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
      setError('');
    } else {
      setError('Please select a valid image file');
    }
  };

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleImageChange(e.dataTransfer.files[0]);
    }
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let productData = {
        ...formData,
        priceValue: parseFloat(formData.priceValue) || 0,
        rating: parseFloat(formData.rating) || 4.5,
        reviews: parseInt(formData.reviews) || 0,
        category: formData.category || ''
      };

      // Add scent notes if any selected category has showScentNotes enabled
      if (shouldShowScentNotes()) {
        productData.notes = {
          top: topNotes.filter(note => note.trim()),
          heart: heartNotes.filter(note => note.trim()),
          base: baseNotes.filter(note => note.trim())
        };
      }

      if (isEditing) {
        await updateProduct(id, productData, imageFile);
      } else {
        await addProduct(productData, imageFile);
      }

      navigate('/admin/products');
    } catch (err) {
      setError(err.message || 'Failed to save product');
      console.error('Save product error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditing) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-brand-gold border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/60 font-body font-light">Loading product...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-black text-brand-light">
      {/* Header */}
      <div className="bg-brand-black/85 border-b border-brand-red/25 shadow-[0_20px_60px_-40px_rgba(230,0,0,0.45)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/admin/products')}
                className="mr-4 p-2 text-brand-light/70 hover:text-brand-gold hover:bg-brand-black/60 rounded-lg transition-colors touch-manipulation"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <h1 className="text-xl lg:text-2xl font-heading font-semibold text-brand-gold">
                {isEditing ? 'Edit Product' : 'Add New Product'}
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-500/20 border border-red-500/40 rounded-xl p-4 flex items-center gap-3">
            <svg className="h-5 w-5 text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-200 font-body">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div className={`${cardClass} mb-8`}>
            <h3 className="text-xl font-heading font-semibold text-brand-gold mb-6">Basic Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelClass}>
                  Product Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className={inputClass}
                  placeholder="Enter product name"
                />
              </div>

              <div>
                <label className={labelClass}>
                  Category *
                </label>
                {loadingCategories ? (
                  <div className="w-full px-4 py-3 bg-brand-black/70 border border-brand-red/30 rounded-xl text-brand-light/60 font-body">
                    Loading categories...
                  </div>
                ) : categories.length === 0 ? (
                  <div className="w-full p-4 bg-brand-red/10 border border-brand-red/30 rounded-xl">
                    <p className="text-brand-light mb-3 font-body">
                      No categories available. Please create categories first.
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate('/admin/categories')}
                      className="px-4 py-2 bg-brand-gold text-brand-dark rounded-lg hover:bg-brand-gold/80 transition-colors font-body font-medium text-sm"
                    >
                      Go to Categories
                    </button>
                  </div>
                ) : (
                  <select
                    name="category"
                    value={formData.category || ''}
                    onChange={handleInputChange}
                    required
                    className={selectClass}
                  >
                    <option value="">Select a category</option>
                    {categories.map(category => (
                      <option 
                        key={category.id} 
                        value={category.id}
                      >
                        {category.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className={labelClass}>
                  Price (R) *
                </label>
                <input
                  type="number"
                  name="priceValue"
                  value={formData.priceValue}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                  className={inputClass}
                  placeholder="300.00"
                />
              </div>

              {/* Gender field - only for perfumes */}
              {(formData.category === 'perfumes' || (formData.category && (() => {
                const cat = categories.find(c => c.id === formData.category);
                return cat?.slug?.includes('perfume') || cat?.name?.toLowerCase().includes('perfume');
              })())) && (
                <div>
                  <label className={labelClass}>
                    Gender
                  </label>
                  <select
                    name="gender"
                    value={formData.gender || 'unisex'}
                    onChange={handleInputChange}
                    className={selectClass}
                  >
                    {GENDER_FILTERS.filter(gender => gender.id !== 'all').map(gender => (
                      <option key={gender.id} value={gender.id}>
                        {gender.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Intensity - only for perfumes */}
              {(formData.category === 'perfumes' || (formData.category && (() => {
                const cat = categories.find(c => c.id === formData.category);
                return cat?.slug?.includes('perfume') || cat?.name?.toLowerCase().includes('perfume');
              })())) && (
                <div>
                  <label className={labelClass}>
                    Intensity
                  </label>
                  <select
                    name="intensity"
                    value={formData.intensity}
                    onChange={handleInputChange}
                    className={selectClass}
                  >
                    {INTENSITY_FILTERS.filter(intensity => intensity.id !== 'all').map(intensity => (
                      <option key={intensity.id} value={intensity.id}>
                        {intensity.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="mt-6">
              <label className={labelClass}>
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                rows="4"
                className={textareaClass}
                placeholder="Enter product description"
              />
            </div>
          </div>

          {/* Product Image */}
          <div className={`${cardClass} mb-8`}>
            <h3 className="text-xl font-heading font-semibold text-brand-gold mb-6">Product Image</h3>
            
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                dragActive 
                  ? 'border-brand-red/60 bg-brand-red/10' 
                  : 'border-brand-red/30 hover:border-brand-red/40 bg-brand-black/70'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {imagePreview ? (
                <div className="space-y-4">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="mx-auto h-48 w-48 object-cover rounded-xl border border-brand-gold/20"
                  />
                  <div className="flex flex-wrap justify-center gap-4">
                    <label className="cursor-pointer bg-brand-red text-brand-light px-6 py-3 rounded-xl hover:bg-brand-red/80 transition-colors font-body font-medium touch-manipulation shadow-[0_20px_45px_-25px_rgba(230,0,0,0.6)]">
                      Change Image
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e.target.files[0])}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview('');
                      }}
                      className="px-6 py-3 border border-brand-red/30 text-brand-light rounded-xl hover:bg-brand-black/60 transition-colors font-body font-medium"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <svg className="mx-auto h-12 w-12 text-brand-light/40" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div>
                    <p className="text-brand-light/70 font-body font-light">Drop an image here, or</p>
                    <label className="cursor-pointer text-brand-gold hover:text-brand-gold/80 font-body font-medium">
                      browse to upload
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e.target.files[0])}
                      />
                    </label>
                  </div>
                  <p className="text-xs text-brand-light/50 font-body font-light">PNG, JPG, GIF up to 10MB</p>
                </div>
              )}
            </div>
          </div>

          {/* Category-Specific Sections */}
          
          {/* Scent Profile - Only for categories with showScentNotes enabled */}
          {shouldShowScentNotes() && (
            <div className={`${cardClass} mb-8`}>
              <h3 className="text-xl font-heading font-semibold text-brand-gold mb-6">Scent Profile</h3>
              
              {/* Top Notes */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className={`${labelClass} mb-0`}>
                    Top Notes
                  </label>
                  <button
                    type="button"
                    onClick={() => addNoteField('top')}
                    className="text-brand-gold hover:text-brand-gold/80 text-sm font-body font-medium"
                  >
                    + Add Note
                  </button>
                </div>
                <div className="space-y-2">
                  {topNotes.map((note, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={note}
                        onChange={(e) => handleNotesChange('top', index, e.target.value)}
                        placeholder="Enter top note"
                        className="flex-1 px-4 py-3 bg-brand-black border border-brand-red/30 rounded-xl text-brand-light placeholder:text-brand-light/50 focus:outline-none focus:ring-2 focus:ring-brand-gold/60 transition-all font-body text-base"
                      />
                      {topNotes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeNoteField('top', index)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Heart Notes (Middle Notes) */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className={`${labelClass} mb-0`}>
                    Middle Notes
                  </label>
                  <button
                    type="button"
                    onClick={() => addNoteField('heart')}
                    className="text-brand-gold hover:text-brand-gold/80 text-sm font-body font-medium"
                  >
                    + Add Note
                  </button>
                </div>
                <div className="space-y-2">
                  {heartNotes.map((note, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={note}
                        onChange={(e) => handleNotesChange('heart', index, e.target.value)}
                        placeholder="Enter middle note"
                        className="flex-1 px-4 py-3 bg-brand-black border border-brand-red/30 rounded-xl text-brand-light placeholder:text-brand-light/50 focus:outline-none focus:ring-2 focus:ring-brand-gold/60 transition-all font-body text-base"
                      />
                      {heartNotes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeNoteField('heart', index)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Base Notes */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className={`${labelClass} mb-0`}>
                    Base Notes
                  </label>
                  <button
                    type="button"
                    onClick={() => addNoteField('base')}
                    className="text-brand-gold hover:text-brand-gold/80 text-sm font-body font-medium"
                  >
                    + Add Note
                  </button>
                </div>
                <div className="space-y-2">
                  {baseNotes.map((note, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={note}
                        onChange={(e) => handleNotesChange('base', index, e.target.value)}
                        placeholder="Enter base note"
                        className="flex-1 px-4 py-3 bg-brand-black border border-brand-red/30 rounded-xl text-brand-light placeholder:text-brand-light/50 focus:outline-none focus:ring-2 focus:ring-brand-gold/60 transition-all font-body text-base"
                      />
                      {baseNotes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeNoteField('base', index)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Category-Specific Attributes */}
          <div className={`${cardClass} mb-8`}>
            <h3 className="text-xl font-heading font-semibold text-brand-gold mb-6">
              Product Details
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Perfume-specific fields */}
              {formData.category === 'perfumes' && (
                <>
                  <div>
                    <label className={labelClass}>
                      Longevity
                    </label>
                    <input
                      type="text"
                      name="longevity"
                      value={formData.longevity}
                      onChange={handleInputChange}
                      placeholder="e.g., 6-8 hours"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>
                      Sillage
                    </label>
                    <select
                      name="sillage"
                      value={formData.sillage}
                      onChange={handleInputChange}
                      className={selectClass}
                    >
                      <option value="Soft">Soft</option>
                      <option value="Moderate">Moderate</option>
                      <option value="Strong">Strong</option>
                      <option value="Very Strong">Very Strong</option>
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>
                      Season
                    </label>
                    <select
                      name="season"
                      value={formData.season}
                      onChange={handleInputChange}
                      className={selectClass}
                    >
                      <option value="All Seasons">All Seasons</option>
                      <option value="Spring/Summer">Spring/Summer</option>
                      <option value="Fall/Winter">Fall/Winter</option>
                    </select>
                  </div>
                </>
              )}

              {/* Candle-specific fields */}
              {formData.category === 'candles' && (
                <>
                  <div>
                    <label className={labelClass}>
                      Burn Time
                    </label>
                    <input
                      type="text"
                      name="burnTime"
                      value={formData.burnTime}
                      onChange={handleInputChange}
                      placeholder="e.g., 40-50 hours"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>
                      Room Size
                    </label>
                    <select
                      name="roomSize"
                      value={formData.roomSize}
                      onChange={handleInputChange}
                      className={selectClass}
                    >
                      <option value="">Select room size</option>
                      <option value="Small (10-20 sqm)">Small (10-20 sqm)</option>
                      <option value="Medium (20-30 sqm)">Medium (20-30 sqm)</option>
                      <option value="Large (30-50 sqm)">Large (30-50 sqm)</option>
                      <option value="Extra Large (50+ sqm)">Extra Large (50+ sqm)</option>
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>
                      Wick Type
                    </label>
                    <select
                      name="wickType"
                      value={formData.wickType}
                      onChange={handleInputChange}
                      className={selectClass}
                    >
                      <option value="">Select wick type</option>
                      <option value="Cotton Wick">Cotton Wick</option>
                      <option value="Wood Wick">Wood Wick</option>
                      <option value="Hemp Wick">Hemp Wick</option>
                    </select>
                  </div>
                </>
              )}

              {/* Diffuser-specific fields */}
              {formData.category === 'diffusers' && (
                <>
                  <div>
                    <label className={labelClass}>
                      Coverage Area
                    </label>
                    <input
                      type="text"
                      name="coverage"
                      value={formData.coverage}
                      onChange={handleInputChange}
                      placeholder="e.g., 25-30 sqm"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>
                      Duration
                    </label>
                    <input
                      type="text"
                      name="duration"
                      value={formData.duration}
                      onChange={handleInputChange}
                      placeholder="e.g., 8-12 weeks"
                    className={inputClass}
                   />
                 </div>

                 <div>
                   <label className={labelClass}>
                     Capacity
                   </label>
                   <select
                     name="capacity"
                     value={formData.capacity}
                     onChange={handleInputChange}
                     className={selectClass}
                   >
                     <option value="">Select capacity</option>
                     <option value="50ml">50ml</option>
                     <option value="100ml">100ml</option>
                     <option value="150ml">150ml</option>
                     <option value="200ml">200ml</option>
                     <option value="250ml">250ml</option>
                   </select>
                 </div>
               </>
             )}

             {/* Room Mist-specific fields */}
             {formData.category === 'room-mist' && (
               <>
                 <div>
                  <label className={labelClass}>
                     Coverage Area
                   </label>
                   <input
                     type="text"
                     name="coverage"
                     value={formData.coverage}
                     onChange={handleInputChange}
                     placeholder="e.g., 20-25 sqm"
                      className={inputClass}
                   />
                 </div>

                 <div>
                  <label className={labelClass}>
                     Duration
                   </label>
                   <input
                     type="text"
                     name="duration"
                     value={formData.duration}
                     onChange={handleInputChange}
                     placeholder="e.g., 4-6 weeks"
                      className={inputClass}
                   />
                 </div>

                 <div>
                  <label className={labelClass}>
                     Capacity
                   </label>
                   <select
                     name="capacity"
                     value={formData.capacity}
                     onChange={handleInputChange}
                     className={inputClass}
                   >
                     <option value="">Select capacity</option>
                     <option value="50ml">50ml</option>
                     <option value="100ml">100ml</option>
                     <option value="150ml">150ml</option>
                     <option value="200ml">200ml</option>
                     <option value="250ml">250ml</option>
                   </select>
                 </div>

                 <div>
                  <label className={labelClass}>
                     Spray Type
                   </label>
                   <select
                     name="sprayType"
                     value={formData.sprayType}
                     onChange={handleInputChange}
                     className={selectClass}
                   >
                     <option value="">Select spray type</option>
                     <option value="Spray Mist">Spray Mist</option>
                     <option value="Fine Mist">Fine Mist</option>
                     <option value="Aerosol Mist">Aerosol Mist</option>
                   </select>
                 </div>
               </>
             )}

             {/* Bath-specific fields */}
             {formData.category === 'bath' && (
               <>
                 <div>
                  <label className={labelClass}>
                     Size
                   </label>
                   <select
                     name="size"
                     value={formData.size}
                     onChange={handleInputChange}
                     className={selectClass}
                   >
                     <option value="">Select size</option>
                     <option value="Small">Small</option>
                     <option value="Regular">Regular</option>
                     <option value="Large">Large</option>
                     <option value="Extra Large">Extra Large</option>
                   </select>
                 </div>

                 <div>
                  <label className={labelClass}>
                     Scent Profile
                   </label>
                   <select
                     name="scentProfile"
                     value={formData.scentProfile}
                     onChange={handleInputChange}
                     className={selectClass}
                   >
                     <option value="">Select scent profile</option>
                     <option value="Floral">Floral</option>
                     <option value="Fresh">Fresh</option>
                     <option value="Woody">Woody</option>
                     <option value="Oriental">Oriental</option>
                     <option value="Fruity">Fruity</option>
                     <option value="Aquatic">Aquatic</option>
                   </select>
                 </div>

                 <div>
                  <label className={labelClass}>
                     Skin Type
                   </label>
                   <select
                     name="skinType"
                     value={formData.skinType}
                     onChange={handleInputChange}
                     className={selectClass}
                   >
                     <option value="">Select skin type</option>
                     <option value="All Skin Types">All Skin Types</option>
                     <option value="Sensitive">Sensitive</option>
                     <option value="Dry">Dry</option>
                     <option value="Oily">Oily</option>
                     <option value="Normal">Normal</option>
                   </select>
                 </div>
               </>
             )}

             {/* Others-specific fields */}
             {formData.category === 'others' && (
               <>
                 <div>
                  <label className={labelClass}>
                     Product Type
                   </label>
                   <select
                     name="productType"
                     value={formData.productType}
                     onChange={handleInputChange}
                     className={selectClass}
                   >
                     <option value="">Select product type</option>
                     <option value="General">General</option>
                     <option value="Accessories">Accessories</option>
                   </select>
                 </div>

                 <div>
                  <label className={labelClass}>
                     Dimensions
                   </label>
                   <input
                     type="text"
                     name="dimensions"
                     value={formData.dimensions}
                     onChange={handleInputChange}
                     placeholder="e.g., 10cm x 5cm x 3cm"
                     className={selectClass}
                   />
                 </div>
               </>
             )}

             {/* Common rating field */}
            <div>
              <label className={labelClass}>
                 Rating
               </label>
               <input
                 type="number"
                 name="rating"
                 value={formData.rating}
                 onChange={handleInputChange}
                 min="0"
                 max="5"
                 step="0.1"
                    className={selectClass}
               />
             </div>
           </div>

           <div className="mt-6 flex items-center space-x-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="inStock"
                checked={formData.inStock}
                onChange={handleInputChange}
                className="w-5 h-5 rounded border-brand-gold/20 bg-brand-dark text-brand-gold focus:ring-brand-gold focus:ring-2"
              />
              <span className="ml-3 text-sm font-body font-medium text-white">In Stock</span>
            </label>

            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="featured"
                checked={formData.featured}
                onChange={handleInputChange}
                className="w-5 h-5 rounded border-brand-gold/20 bg-brand-dark text-brand-gold focus:ring-brand-gold focus:ring-2"
              />
              <span className="ml-3 text-sm font-body font-medium text-white">Featured Product</span>
            </label>
           </div>
         </div>

        {/* Form Actions */}
        <div className="flex flex-col sm:flex-row justify-end gap-4 pt-6 border-t border-brand-gold/20">
          <button
            type="button"
            onClick={() => navigate('/admin/products')}
            className="px-6 py-3 border border-brand-gold/30 text-white rounded-xl hover:bg-brand-charcoal transition-colors font-body font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-brand-gold text-brand-dark rounded-xl hover:bg-brand-gold-light disabled:bg-brand-charcoal disabled:text-white/40 transition-colors flex items-center justify-center font-body font-medium touch-manipulation"
          >
            {loading && (
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {isEditing ? 'Update Product' : 'Create Product'}
          </button>
        </div>
       </form>
     </div>
   </div>
 );
};

export default AdminProductForm;